import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { context } from 'bookingbug-core-js';

/**
 * BMBC IMS pre-basket (book-after-payment) integration for Barnsley —
 * talks to bl-barnsley-ims-api (`/payment/bmbc-ims/pre-basket*`).
 * See bl-barnsley-ims-api/docs/FRONTEND-INTEGRATION.md.
 *
 * Flow:
 *   1. preBasketInvoke — stores the booking context in DynamoDB, creates a
 *      Barnsley IMS payment session and returns the hosted-page redirect `url`.
 *   2. The customer pays on the Barnsley hosted page.
 *   3. Barnsley redirects to the gateway's /return; the gateway hands the
 *      booking context to the n8n post-basket workflow (JRNI checkout + pay
 *      happens server-side) and 302s the customer back to our return_url
 *      (success) or back_url (failure) with the result appended as query
 *      params — PaymentAuthorisationCode === '00000' means paid.
 *   4. The verify page reads those params and polls preBasketFind
 *      (INITIATED → INVOKED → RETURNED → HANDED_OFF, then PAID with
 *      data.purchase_id once the post-basket worker records the purchase).
 *
 * Multi-line: one IMS line item is created per amounts[] entry — the plural
 * arrays amounts/descriptions/fund_codes/vat_codes are parallel. The gateway
 * derives AccountReference/Narrative/FundCode from service_type for known
 * types (ceremony, copy_certificate, notice_of_marriage, register_birth);
 * per-line overrides win only when non-empty, so blank gl_code/vat_code
 * entries safely fall back to the reference map.
 */
@Injectable({
  providedIn: 'root',
})
export class BarnsleyImsService {
  // Staging gateway — the production base URL is TBD (FRONTEND-INTEGRATION.md
  // §2) and must be swapped in here when the prod gateway is deployed.
  private url = 'https://staging.bookinglab.co.uk';
  // private url = 'http://localhost:4201';
  private path = '/';
  private gatewayPath = '/payment/bmbc-ims';

  constructor(private http: HttpClient) {}

  async preBasketInvoke(
    company,
    service,
    basket,
    serviceItems,
    descriptions,
    client,
    fundCode,
    amounts,
    authToken,
    data,
    detailGroupId,
    slot,
    extraDuration,
    journeyRoute,
    reference,
    parentCompanyId?,
    options?: {
      accessChannel?: string;
      callingRecordingSource?: string;
      address?: any;
      postcode?: string;
      answers?: any;
    }
  ) {
    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    let returnUrl = '';
    if (journeyRoute == 'order_a_copy_certificate') {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    const body: any = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT company. child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      service_id: `${service.id}`,
      client_id: `${client.id}`,
      // Tenant slug the gateway keys its table/tokens by; it also builds the
      // IMS return endpoint as /return/barnsley-new-staging/… and forwards this
      // as body.client to the post-basket workflow. Staging-only, like this.url
      // — swap for the prod slug when the prod gateway is wired.
      client: 'barnsley-new-staging',
      basket: `${basket.id}`,
      service_items: serviceItems,
      data: data,
      return_url: `${returnUrl}`,
      // The gateway appends the payment result params to back_url on
      // failure/cancel; the ?cancelled=true marker is kept as a safety net so
      // a redirect that somehow arrives without params still restarts the
      // journey instead of stranding the user on the verify spinner (NRPO-38).
      back_url: `${returnUrl}?cancelled=true`,
      // The gateway requires auth_token; the certificate journey has no JRNI
      // basket auth token, so send a placeholder it can ignore.
      auth_token: authToken ? `${authToken}` : 'n/a',
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute,
      service_type: this.serviceTypeFor(journeyRoute),
    };

    // Customer details for the payment record (the gateway falls back to the
    // JRNI client for name/email if these are omitted).
    if (client && client.first_name) {
      body.first_name = `${client.first_name}`;
    }
    if (client && client.last_name) {
      body.last_name = `${client.last_name}`;
    }
    if (client && client.email) {
      body.email = `${client.email}`;
    }

    // Billing address for the payment record and the CyberSource AVS check behind
    // Barnsley IMS. The gateway only falls back to the JRNI client address1..4 /
    // postcode when these keys are absent, and neither journey ever writes an
    // address onto the JRNI client — so without this the card provider receives no
    // street and no postcode. Empty parts are omitted rather than sent as '', so
    // the client fallback still applies to them.
    if (options && (options.address || options.postcode || options.answers)) {
      const billing = this.addressFieldsFrom(
        options.address,
        options.postcode || this.postcodeFromAnswers(options.answers)
      );
      if (billing.house_name_or_number) {
        body.house_name_or_number = billing.house_name_or_number;
      }
      if (billing.street) {
        body.street = billing.street;
      }
      if (billing.town) {
        body.town = billing.town;
      }
      if (billing.post_code) {
        body.post_code = billing.post_code;
      }
    }

    // Mediated (agent-on-behalf) payments.
    if (options && options.accessChannel) {
      body.access_channel = options.accessChannel;
      if (options.callingRecordingSource) {
        body.calling_recording_source = options.callingRecordingSource;
      }
    }

    // ---- Line items ----------------------------------------------------
    // Callers pass either a scalar amount (the ceremonies deposit path) or an
    // array of Capita-era charge objects { charge, chargeName, glCode?,
    // vatCode? } (occasionally bare numbers). IMS wants parallel plural
    // arrays of amounts (major units) and descriptions, so normalise here —
    // taking each line's description from its own chargeName, which is
    // guaranteed to line up with its charge.
    if (Array.isArray(amounts)) {
      const lineAmounts = [];
      const lineDescriptions = [];
      const lineFundCodes = [];
      const lineVatCodes = [];
      let hasFundCode = false;
      let hasVatCode = false;
      amounts.forEach((entry, i) => {
        if (entry && typeof entry === 'object') {
          lineAmounts.push(Number(entry.charge) || 0);
          lineDescriptions.push(`${entry.chargeName || descriptions[i] || descriptions[0] || ''}`);
          lineFundCodes.push(entry.glCode ? `${entry.glCode}` : '');
          lineVatCodes.push(entry.vatCode ? `${entry.vatCode}` : '');
          if (entry.glCode) {
            hasFundCode = true;
          }
          if (entry.vatCode) {
            hasVatCode = true;
          }
        } else {
          lineAmounts.push(Number(entry) || 0);
          lineDescriptions.push(`${descriptions[i] || descriptions[0] || ''}`);
          lineFundCodes.push('');
          lineVatCodes.push('');
        }
      });
      body.amounts = lineAmounts;
      body.descriptions = lineDescriptions;
      if (hasFundCode) {
        body.fund_codes = lineFundCodes;
      }
      if (hasVatCode) {
        body.vat_codes = lineVatCodes;
      }
      // Service-level fund code acts as the per-line fallback on the gateway
      // (fund_codes[i] || fund_code || reference-map default).
      if (fundCode && !Array.isArray(fundCode)) {
        body.fund_code = `${fundCode}`;
      }
    } else {
      body.amount = Number(amounts) || 0;
      body.description = `${descriptions[0] || service.name || ''}`;
      if (fundCode && !Array.isArray(fundCode)) {
        body.fund_code = `${fundCode}`;
      }
    }

    const result: any = await this.http
      .post(`${this.url}${this.gatewayPath}/pre-basket`, body)
      .toPromise();
    return result;
  }

  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      // Same tenant slug as pre-basket, so find hits the new client-keyed table.
      client: 'barnsley-new-staging',
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http
      .post(`${this.url}${this.gatewayPath}/pre-basket/find`, body)
      .toPromise();
  }

  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      // Same tenant slug as pre-basket, so update hits the new client-keyed table.
      client: 'barnsley-new-staging',
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data,
    };
    return this.http
      .post(`${this.url}${this.gatewayPath}/pre-basket/update`, body)
      .toPromise();
  }

  private squash(value): string {
    return `${value === undefined || value === null ? '' : value}`
      .replace(/\s+/g, ' ')
      .trim();
  }

  private isPostcode(value): boolean {
    return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(this.squash(value));
  }

  // Split the customer's address into the flat fields the gateway wants. What the
  // journey actually holds is the single OS Places "ADDRESS" line the lookup
  // control emits — "BARNSLEY TOWN HALL, CHURCH STREET, BARNSLEY, S70 2TA" — so
  // peel it from the end: postcode, town, street, and whatever is left is the
  // house/name part. Barnsley joins house_name_or_number + street with a space
  // into CyberSource's bill_to_address_line1, so an imperfect house/street split
  // is harmless; what must be right is that town and postcode come out on their
  // own. `locality` is deliberately left empty — the gateway maps it to
  // bill_to_address_state and drops it.
  private addressFieldsFrom(address, fallbackPostcode?) {
    const out = {
      house_name_or_number: '',
      street: '',
      locality: '',
      town: '',
      post_code: '',
    };

    if (address && typeof address === 'object') {
      // An already-structured address (manual / international entry).
      out.house_name_or_number = this.squash(
        address.house_name_or_number ||
          address.houseNameOrNumber ||
          address.addressLine1 ||
          address.address_line_1 ||
          address.address1
      );
      out.street = this.squash(
        address.street ||
          address.addressLine2 ||
          address.address_line_2 ||
          address.address2
      );
      out.locality = this.squash(address.locality || address.address3);
      out.town = this.squash(address.town || address.city || address.address4);
      out.post_code = this.squash(
        address.post_code ||
          address.postCode ||
          address.postcode ||
          address.addressPostcodeLookup
      );

      // Some callers hand over a whole customer-details object whose UK branch
      // keeps the address as one lookup string.
      if (!out.house_name_or_number && !out.town) {
        const line = address.address || address.full_address || address.ADDRESS;
        if (line) {
          return this.addressFieldsFrom(
            `${line}`,
            fallbackPostcode || out.post_code
          );
        }
      }
    } else {
      const parts = this.squash(address)
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

      if (parts.length > 0) {
        if (this.isPostcode(parts[parts.length - 1])) {
          out.post_code = parts.pop();
        } else if (parts.length === 1) {
          // A hand-typed single line ("12 Church Street S70 2TA") has no commas,
          // so peel a trailing postcode off the end of it instead.
          const trailing = parts[0].match(
            /\s([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})$/i
          );
          if (trailing) {
            out.post_code = trailing[1];
            parts[0] = parts[0].slice(0, trailing.index).trim();
          }
        }

        if (parts.length >= 3) {
          out.town = parts.pop();
          out.street = parts.pop();
        } else if (parts.length === 2) {
          out.town = parts.pop();
        }
        out.house_name_or_number = parts.join(' ').trim();
      }
    }

    if (!out.post_code) {
      out.post_code = this.squash(fallbackPostcode);
    }
    out.post_code = out.post_code.toUpperCase();

    return out;
  }

  // The address-lookup control keeps the postcode the customer typed in a sibling
  // `<field>_postcode` control. That is the only address data we have if they
  // searched but never picked a result — and the postcode is the field AVS keys
  // on, so it is worth sending on its own.
  private postcodeFromAnswers(answers): string {
    if (!answers || typeof answers !== 'object') {
      return '';
    }
    for (const key of Object.keys(answers)) {
      if (!/_postcode$/.test(key) && !/^postcode_/.test(key)) {
        continue;
      }
      const value = this.squash(answers[key]);
      if (value && this.isPostcode(value)) {
        return value;
      }
    }
    return '';
  }

  // Map an app journey route onto the gateway's service_type reference map
  // (which resolves AccountReference/Narrative/FundCode). Unknown types are
  // passed through — the gateway 400s on them unless account_reference and
  // narrative overrides are supplied, or the type is added to its map.
  private serviceTypeFor(journeyRoute): string {
    const route = `${journeyRoute || ''}`;
    if (route === 'order_a_copy_certificate') {
      return 'copy_certificate';
    }
    return route;
  }
}
